/**
 * YAML Frontmatter Editor Component
 * Provides a modal dialog for editing file metadata in YAML format
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileCode, AlertCircle, Check, X } from 'lucide-react';
import {
  parseFrontmatter,
  stringifyFrontmatter,
} from '@/core/utils/frontmatter';
import type { FileMetadata } from '@phrasewright/types';
import * as yaml from 'js-yaml';

interface FrontMatterEditorProps {
  content: string;
  onSave: (newContent: string) => Promise<void>;
  children?: React.ReactNode;
}

export function FrontMatterEditor({
  content,
  onSave,
  children,
}: FrontMatterEditorProps) {
  const [open, setOpen] = useState(false);
  const [yamlContent, setYamlContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      try {
        const parsed = parseFrontmatter(content);
        const yamlString = yaml.dump(parsed.metadata || {});
        setYamlContent(yamlString);
        setHasChanges(false);
        setError(null);
      } catch (err) {
        setError('Failed to parse frontmatter');
      }
    }
  }, [open, content]);

  const handleYamlChange = (value: string) => {
    setYamlContent(value);
    setHasChanges(true);
    setError(null);

    // Validate YAML syntax
    try {
      yaml.load(value);
    } catch (err) {
      if (err instanceof Error) {
        setError(`Invalid YAML: ${err.message}`);
      }
    }
  };

  const handleSave = async () => {
    if (error) return;

    try {
      setIsSaving(true);

      // Parse the YAML to validate and get metadata
      const metadata = yaml.load(yamlContent) as FileMetadata;

      // Parse existing content to get the body
      const parsed = parseFrontmatter(content);

      // Create new content with updated frontmatter
      const newContent = stringifyFrontmatter(metadata, parsed.content);

      await onSave(newContent);
      setOpen(false);
      setHasChanges(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save frontmatter'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmed) return;
    }
    setOpen(false);
    setError(null);
    setHasChanges(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm" className="h-8">
            <FileCode className="h-3 w-3 mr-1" />
            Edit Frontmatter
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Frontmatter</DialogTitle>
          <DialogDescription>
            Edit the YAML frontmatter metadata for this file. This includes
            type, tags, and other properties.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="yaml-editor">YAML Frontmatter</Label>
            <Textarea
              id="yaml-editor"
              value={yamlContent}
              onChange={(e) => handleYamlChange(e.target.value)}
              placeholder="type: document
title: My Document
tags:
  - example
  - documentation
description: A sample document"
              className="font-mono text-sm h-64 resize-none"
              spellCheck={false}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="text-sm text-muted-foreground space-y-1">
            <p>Common properties:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  type
                </code>
                : document | phrase
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  title
                </code>
                : Display title
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  tags
                </code>
                : Array of tags
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  description
                </code>
                : Brief description
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  author
                </code>
                : Author name
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  created
                </code>
                : Creation date
              </li>
              <li>
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  updated
                </code>
                : Last update date
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!!error || isSaving || !hasChanges}
          >
            <Check className="h-4 w-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
